import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
  FC,
} from 'react';
import Draggable from 'react-draggable';
import { shallowEqual, useSelector } from 'react-redux';
import GrabAreaGrayIcon from 'spw-static/images/grab-area-gray.svg';
import {
  projectColors as selectProjectColors,
  selectProjectSpaceTypeCategories,
} from 'spw-redux/modules/projects/selectors';
import { IProjectSpaceTypeCategory } from 'spw-redux/modules/projects/types';
import { colors } from 'spw-theme';
import {
  GrabArea,
  LargeGrabArea,
  NavigationButtonsContainer,
  ButtonsContainer,
  ScrollToTopButton,
  GrabAreaImg,
  LargeGrabAreaImg,
  NavigationButton,
} from 'spw-projects/programming/program-summary/styles';
import { HEADER_HEIGHT_IN_PX } from 'spw-components/header/styles';
import { getColor } from 'spw-shared/utils';

interface IGrabberProps {
  id?: string;
}

export const Grabber: FC<IGrabberProps> = ({ id }) => (
  <GrabArea id={id}>
    <GrabAreaImg src={GrabAreaGrayIcon} alt="grab navigation buttons" />
  </GrabArea>
);

export const LargeGrabber: FC<IGrabberProps> = ({ id }) => (
  <LargeGrabArea id={id}>
    <LargeGrabAreaImg src={GrabAreaGrayIcon} alt="grab navigation buttons" />
  </LargeGrabArea>
);

interface DraggableNavigationMenuProps {
  refMap: Record<number, React.MutableRefObject<HTMLDivElement>>;
  parent: MutableRefObject<HTMLDivElement>;
  spaceTypeCategories?: IProjectSpaceTypeCategory[];
  getNavigationButtonProps?: (
    spaceTypeCategory: IProjectSpaceTypeCategory,
    scrollTo: (id: number) => void
  ) => Record<string, unknown>;
}

const MIN_RIGHT_DISTANCE = 8;
const MIN_TOP_DISTANCE = 8;

const DraggableNavigationMenu: React.FunctionComponent<DraggableNavigationMenuProps> = ({
  refMap,
  parent,
  spaceTypeCategories,
  getNavigationButtonProps = () => ({}),
}: DraggableNavigationMenuProps) => {
  const bottom = 72;
  const navigationMenuHeight = 226;
  const navigationMenuWidth = 50;

  const navigationMenuRef = useRef(null);

  // Function to get the screen width without the scrollbar width
  const getScreenWidth = useCallback(
    (parentRef: MutableRefObject<HTMLDivElement>) => {
      const { x: parentX } = parentRef.current.getBoundingClientRect();
      const { width } = parentRef.current.getBoundingClientRect();
      return width + 2 * parentX;
    },
    []
  );

  const [bounds, setBounds] = useState({
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
  });

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isScrollToTopDisabled, setIsScrollToTopDisabled] = useState(true);

  const isScrollToTopDisabledRef = useRef(true);
  const positionRef = useRef({ x: 0, y: 0 });
  positionRef.current = position;
  isScrollToTopDisabledRef.current = isScrollToTopDisabled;

  const projectSpaceTypeCategories =
    spaceTypeCategories ||
    useSelector(selectProjectSpaceTypeCategories, shallowEqual);
  const projectColors = useSelector(selectProjectColors);

  const onWindowsResize = useCallback(() => {
    if (parent.current) {
      const screenWidth = getScreenWidth(parent);

      // Get the widget position (x,y), it is a negative value since the origin is 8px (MAX_RIGHT_DISTANCE)
      // to the left of the window and 40px (bottom) above the bottom edge.

      // This will stop the widget from leaving the screen when the window is resized
      setPosition({
        // Get the max value of the current x-value and the
        // x-value of the widget when it is placed on the left of the window
        x: Math.max(
          positionRef.current.x,
          (screenWidth - navigationMenuWidth - MIN_RIGHT_DISTANCE) * -1
        ),

        // Get the min value of the result of the Math.max function and the max y-value
        // that the widget can have at the bottom of the window
        y: Math.min(
          // Get the max value of the current y-value and
          // the y-value of the widget when it is placed on the top of the window
          Math.max(
            positionRef.current.y,
            (window.innerHeight - bottom - navigationMenuHeight) * -1
          ),
          bottom
        ),
      });

      // The top bound is calculated by adding up the current negative y-value and the y-value of
      // the position relative to the viewport
      const topBound =
        -(
          navigationMenuRef.current.getBoundingClientRect().y +
          positionRef.current.y * -1
        ) + HEADER_HEIGHT_IN_PX;

      setBounds({
        left: (screenWidth - navigationMenuWidth - MIN_RIGHT_DISTANCE) * -1,
        right: MIN_RIGHT_DISTANCE,
        bottom,
        top: topBound,
      });
    }
  }, [parent, getScreenWidth]);

  const handleScroll = useCallback(() => {
    if (window.scrollY === 0) {
      setIsScrollToTopDisabled(true);
      return;
    }

    if (isScrollToTopDisabledRef.current) {
      setIsScrollToTopDisabled(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', onWindowsResize);
    window.addEventListener('scroll', handleScroll);

    if (parent.current) {
      const { x: parentX, width } = parent.current.getBoundingClientRect();
      const screenWidth = getScreenWidth(parent);
      const marginLeft = 8;

      // Place the component 8px to the right of the parent
      let x =
        (screenWidth -
          width -
          parentX -
          navigationMenuWidth -
          marginLeft -
          MIN_RIGHT_DISTANCE) *
        -1;

      // On smaller screens, always position the widget 8px to the right of the window
      if (
        screenWidth <
        parentX + width + navigationMenuWidth + marginLeft + MIN_RIGHT_DISTANCE
      ) {
        x = 0;
      }

      setPosition({
        x,
        y: Math.max(
          (window.innerHeight -
            bottom -
            navigationMenuHeight -
            MIN_TOP_DISTANCE) *
            -1,
          0
        ),
      });

      setBounds({
        left:
          (2 * parent.current.getBoundingClientRect().x +
            parent.current.getBoundingClientRect().width -
            MIN_RIGHT_DISTANCE -
            navigationMenuWidth) *
          -1,
        right: MIN_RIGHT_DISTANCE,
        bottom,
        top:
          -(window.innerHeight - bottom - navigationMenuHeight) +
          HEADER_HEIGHT_IN_PX,
      });
    }

    return () => {
      window.removeEventListener('resize', onWindowsResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [parent, getScreenWidth, onWindowsResize, handleScroll]);

  const scrollTo = useCallback(
    (id: number) => {
      if (refMap[id]?.current) {
        refMap[id].current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    },
    [refMap]
  );

  const scrollToTop = () => {
    if (window.scrollY === 0) {
      return;
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  const handleStop = (event: MouseEvent, data: { x: number; y: number }) => {
    setPosition({
      x: data.x,
      y: data.y,
    });
  };

  return (
    <Draggable
      bounds={bounds}
      position={position}
      onStop={handleStop}
      nodeRef={navigationMenuRef}
    >
      <NavigationButtonsContainer
        ref={navigationMenuRef}
        style={{
          zIndex: 2,
          position: 'fixed',
          bottom,
          right: MIN_RIGHT_DISTANCE, // The widget is positioned 8px to the right of the window, these coordinates will be its origin
        }}
      >
        <Grabber />
        <ButtonsContainer>
          {projectSpaceTypeCategories?.map(
            (projectSpaceTypeCategory: IProjectSpaceTypeCategory) => (
              <NavigationButton
                key={`color-dot-${projectSpaceTypeCategory.id}`}
                color={getColor(
                  projectSpaceTypeCategory.defaultSpaceTypeCategory.category,
                  projectColors
                )}
                onClick={() => {
                  scrollTo(projectSpaceTypeCategory.id);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                {...getNavigationButtonProps(
                  projectSpaceTypeCategory,
                  scrollTo
                )}
              >
                <img
                  src={`/images/${projectSpaceTypeCategory.icon}.svg`}
                  alt="Project Space Type Category Color Dot"
                />
              </NavigationButton>
            )
          )}

          <ScrollToTopButton
            color={colors.white}
            onClick={scrollToTop}
            className={isScrollToTopDisabled ? 'disabled' : ''}
          >
            <img
              src={
                isScrollToTopDisabled
                  ? '/images/arrow_upward_disabled.svg'
                  : '/images/arrow_upward.svg'
              }
              alt="arrow-upward"
            />
          </ScrollToTopButton>
        </ButtonsContainer>
      </NavigationButtonsContainer>
    </Draggable>
  );
};

export default DraggableNavigationMenu;
